import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom } from 'rxjs/operators';
import { DeviceDetailService, InverterV2, MonitoringType, ProductModelService } from 'ngx-core-services';

import {
  AppState,
  SystemsState,
  getSystemDefinitionConfiguration,
  getSystemDefinitionDeviceIdsForTelemetries,
  requestSystemAlertsCount,
  setSystemAlertsCountError,
  updateSystemAlertsCount,
  } from '..';
import { processAndGroupAlerts } from '../../lib/alerts/alert.utils';
import { AlertContent } from '../../lib/alerts/alert.model';

const ALERTS_COUNTER_DEBUG = false;
const TAG = '[AlertsCounter]';
const log = (...args: any[]) => { if (ALERTS_COUNTER_DEBUG) { console.log(...args); } };

@Injectable()
export class AlertsCounterEffects {

  requestAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemAlertsCount),
      withLatestFrom(this.store),
      mergeMap(([action, { systemState }]) =>
        from(this.getSystemAlerts(action.systemId, action.forceRefresh, action.showAll, systemState)).pipe(
          map((alertCounter) =>
            updateSystemAlertsCount({ systemId: action.systemId, instanceId: action.instanceId, alerts: alertCounter })
          ),
          catchError(error =>
            of(setSystemAlertsCountError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system alerts' }))
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private readonly deviceDetailService: DeviceDetailService,
  ) {}

  async getSystemAlerts(systemId: string, forceRefresh: boolean, showAll: boolean, state: SystemsState): Promise<AlertContent> {
    const system = state.systemList[systemId];
    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);

    const serials = getSystemDefinitionDeviceIdsForTelemetries(system, { onlyInstalled: false , onlyInvertersOfEmsMonitoring: true });

    log(`${TAG} systemId=${systemId}, gwType=${systemConfiguration.monitoringType}, serials=${serials}, showAll=${showAll}`);

    const deviceStatuses = await this.deviceDetailService.fetchDevicesStatusInfo(serials, systemConfiguration.monitoringType, forceRefresh);

    const adaptTelemetry: { [serial: string]: Partial<InverterV2>[] } = {};
    // Filter by gateway source (same logic as PowersService.filterBySourceData)
    // and skip stale telemetry (older than 24h) — alerts from offline devices are misleading
    const gwType = systemConfiguration.monitoringType;
    const staleThreshold = Math.floor(Date.now() / 1000) - 24 * 60 * 60;
    for (const serial in deviceStatuses) {
      if (deviceStatuses.hasOwnProperty(serial)) {
        const status = deviceStatuses[serial];
        const isStale = !status.TS || status.TS < staleThreshold;
        const accepted = !isStale && this.isFromSystemSource(status, gwType);
        log(`${TAG} serial=${serial}, GwId=${status.GwId}, TS=${status.TS}, isStale=${isStale}, accepted=${accepted}`);
        if (accepted) {
          adaptTelemetry[serial] = [status];
        }
      }
    }
    return processAndGroupAlerts(adaptTelemetry, { showAll }).alerts;
  }

  /**
   * Same filtering logic as PowersService.filterBySourceData — ensures
   * the counter only counts alerts coming from the system's monitoring gateway type.
   */
  private isFromSystemSource(status: Partial<InverterV2>, gwType: MonitoringType): boolean {
    const gwId = status.GwId;
    if (!gwId) {
      return gwType === 'Solarman';
    }
    const loggerProduct = new ProductModelService(gwId);
    if (gwType === 'Connext') {
      return loggerProduct.type?.name === 'connext';
    } else if (gwType === 'AzzurroHub') {
      return loggerProduct.type?.name === 'azzurro-hub';
    }
    return loggerProduct.type?.name !== 'connext';
  }
}
